import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CornerDownLeft, Search, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useCapabilities } from "@/lib/capabilities";
import { SECTIONS } from "@/components/layout/Sidebar";

const TCODE_RE = /^[A-Za-z][A-Za-z0-9_/-]{1,19}$/;

/** Global command palette (⌘K / Ctrl+K). Searches the module map, workspaces,
 * engineered roles and findings, plus a direct "analyse this T-code" action. */
export function CommandPalette({ open, onClose }) {
    const nav = useNavigate();
    const { caps } = useCapabilities();
    const [q, setQ] = useState("");
    const [cursor, setCursor] = useState(0);
    const [data, setData] = useState({ workspaces: [], roles: [], findings: [] });
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setQ("");
        setCursor(0);
        const t = setTimeout(() => inputRef.current?.focus(), 40);
        (async () => {
            const [ws, roles, findings] = await Promise.all([
                api.get("/workspaces").catch(() => ({ data: [] })),
                api.get("/roles").catch(() => ({ data: [] })),
                api.get("/findings").catch(() => ({ data: [] })),
            ]);
            setData({
                workspaces: ws.data || [],
                roles: roles.data || [],
                findings: (findings.data || []).slice(0, 300),
            });
        })();
        return () => clearTimeout(t);
    }, [open]);

    const navItems = useMemo(
        () =>
            SECTIONS(caps)
                .flatMap((s) => s.items.filter((i) => i.show).map((i) => ({ ...i, section: s.label })))
                .map((i) => ({
                    kind: "module",
                    id: i.slug,
                    label: i.label,
                    hint: i.section,
                    icon: i.icon,
                    to: i.to,
                })),
        [caps],
    );

    const results = useMemo(() => {
        const needle = q.trim().toLowerCase();
        const out = [];
        const matches = (s) => (s || "").toLowerCase().includes(needle);

        const modules = needle ? navItems.filter((i) => matches(i.label) || matches(i.hint)) : navItems.slice(0, 8);
        out.push(...modules.slice(0, 8));

        if (needle) {
            if (TCODE_RE.test(q.trim()) && (caps?.can_instant_analysis ?? true)) {
                out.push({
                    kind: "action",
                    id: `ia-${q}`,
                    label: `Analyse “${q.trim().toUpperCase()}” — T-code / Fiori App`,
                    hint: "Instant Analysis",
                    icon: Zap,
                    to: `/instant-analysis?q=${encodeURIComponent(q.trim())}`,
                });
            }
            out.push(
                ...data.workspaces
                    .filter((w) => matches(w.name) || matches(w.description))
                    .slice(0, 5)
                    .map((w) => ({
                        kind: "workspace",
                        id: w.id,
                        label: w.name,
                        hint: `Workspace · ${w.finding_count ?? 0} findings`,
                        to: `/workspaces/${w.id}`,
                    })),
            );
            out.push(
                ...data.roles
                    .filter((r) => matches(r.name) || (r.tcodes || []).some((t) => matches(t)))
                    .slice(0, 5)
                    .map((r) => ({
                        kind: "role",
                        id: r.id,
                        label: r.name,
                        hint: `Role · ${(r.tcodes || []).length} T-codes · ${(r.sod_conflicts || []).length} SoD`,
                        to: `/roles?expand=${r.id}`,
                    })),
            );
            out.push(
                ...data.findings
                    .filter((f) => matches(f.title) || matches(f.category))
                    .slice(0, 5)
                    .map((f) => ({
                        kind: "finding",
                        id: f.id,
                        label: f.title,
                        hint: `Finding · ${f.severity} · ${f.engine}`,
                        to: `/findings?severity=${f.severity}&engine=${f.engine}`,
                    })),
            );
        }
        return out;
    }, [q, navItems, data, caps]);

    useEffect(() => setCursor(0), [q]);

    const go = useCallback(
        (item) => {
            if (!item) return;
            onClose();
            nav(item.to);
        },
        [nav, onClose],
    );

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setCursor((c) => Math.min(c + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setCursor((c) => Math.max(c - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            go(results[cursor]);
        } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
        }
    };

    useEffect(() => {
        listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
    }, [cursor, results]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="command-palette"
                    className="fixed inset-0 z-[80] flex items-start justify-center pt-[12vh] px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                >
                    <div
                        className="absolute inset-0 bg-black/70 backdrop-blur-md"
                        onClick={onClose}
                        data-testid="command-palette-overlay"
                    />
                    <motion.div
                        className="relative w-full max-w-[680px] hud-glass chrome-bevel rounded-[6px] overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.8)]"
                        initial={{ opacity: 0, y: -14, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.985 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/8">
                            <Search size={16} className="text-cyan-400/80 shrink-0" />
                            <input
                                ref={inputRef}
                                data-testid="command-palette-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={onKeyDown}
                                placeholder="Jump to a module, workspace, role, finding or T-code…"
                                className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none"
                            />
                            <kbd className="font-mono text-[10px] text-slate-500 border border-slate-700 px-1.5 py-0.5 rounded-sm">ESC</kbd>
                        </div>

                        <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-2">
                            {results.length === 0 && (
                                <div className="px-4 py-8 text-center text-xs font-mono text-slate-500" data-testid="command-palette-empty">
                                    Nothing matches “{q}”.
                                </div>
                            )}
                            {results.map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={`${item.kind}-${item.id}`}
                                        data-testid={`command-palette-item-${item.kind}-${item.id}`}
                                        data-active={i === cursor}
                                        onMouseEnter={() => setCursor(i)}
                                        onClick={() => go(item)}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                                            i === cursor ? "bg-cyan-400/10" : "hover:bg-white/[0.03]"
                                        }`}
                                    >
                                        <span
                                            className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 border ${
                                                i === cursor ? "border-cyan-400/40 text-cyan-300" : "border-white/10 text-slate-500"
                                            }`}
                                        >
                                            {Icon ? <Icon size={13} strokeWidth={1.6} /> : <span className="font-mono text-[9px] uppercase">{item.kind[0]}</span>}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-[13px] text-white truncate">{item.label}</span>
                                            <span className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 truncate">{item.hint}</span>
                                        </span>
                                        {i === cursor && <CornerDownLeft size={13} className="text-cyan-400/70 shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="px-4 py-2.5 border-t border-white/8 flex items-center gap-4 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                            <span>↑↓ navigate</span>
                            <span>⏎ open</span>
                            <span className="ml-auto">{results.length} result{results.length === 1 ? "" : "s"}</span>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/** ⌘K / Ctrl+K global hotkey. Returns [open, setOpen]. */
export function useCommandPalette() {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((o) => !o);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    return [open, setOpen];
}
